import is from '@sindresorhus/is'

export default class Calculator {
  constructor (data) {
    this.data = data
    this.unit = data.unit
  }

  currencyFormatter = new Intl.NumberFormat(
    undefined, // Use default locale
    {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }
  )

  sqftToSqm (value) {
    return Math.round(value / 10.764)
  }
  psqftToPsqm (value) {
    return Math.round(value * 10.764)
  }

  sqmToSqft (value) {
    return Math.round(value * 10.764)
  }
  psqmToPsqft (value) {
    return Math.round(value / 10.764)
  }

  sqftToAcres (value) {
    return value / 43560
  }
  sqmToHectares (value) {
    return value / 10000
  }

  acresToSqft (value) {
    return value * 43560
  }
  hectaresToSqm (value) {
    return value * 10000
  }

  hectaresToAcres (value) {
    return Math.round(10 * value * 2.471) / 10
  }
  acresToHectares (value) {
    return Math.round(10 * value / 2.471) / 10
  }

  format (value) {
    if (is.number(value) && ! is.nan(value)) {
      return this.currencyFormatter.format(value)
    }

    if (is.nan(value)) {
      return '—'
    }

    return null
  }

  // Unit labels

  getUnitAreaLabel (large = false) {
    if (this.unit === 'metric') {
      if (large) {
        return 'ha'
      } else {
        return 'sq m'
      }
    } else if (this.unit === 'imperial') {
      if (large) {
        return 'ac'
      } else {
        return 'sq ft'
      }
    }

    return 'unit area'
  }

  getPerUnitAreaLabel () {
    if (this.unit === 'metric') {
      return 'per sq m'
    } else if (this.unit === 'imperial') {
      return 'per sq ft'
    }

    return 'per unit area'
  }

  convertToLargeArea (area) {
    if (this.unit === 'metric') {
      return this.sqmToHectares(area)
    } else if (this.unit === 'imperial') {
      return this.sqftToAcres(area)
    }

    return area
  }

  // Data Helpers

  getValue (field) {
    const value = this.data[field]

    if (is.number(value)) {
      return value
    }

    if (is.object(value)) {
      if (is.number(value.value)) {
        return value.value
      }
    }

    return NaN
  }

  getPercentageOrFixedValue (field, total) {
    if (this.data[field].unit.startsWith('percent')) {
      return this.getValue(field) / 100 * total
    } else if (this.data[field].unit === 'fixed') {
      return this.getValue(field)
    }

    return NaN
  }

  // Global Data

  /**
   * What is the value for the total area of the development (in the current calculation's standard
   * units)?
   *
   * This is used to calculate totals in a "per unit area" calculation.
   *
   * @returns {number}
   */
  getRawTotalArea () {
    throw new Error('You must implement getRawTotalArea')
  }

  /**
   * The total area of actual land.
   *
   * @param {bool} large Whether to return the value in large areas (hectares/acres).
   */
  getTotalArea (large = true) {
    const totalArea = this.getRawTotalArea()

    if (! large) {
      if (this.unit === 'metric') {
        return this.hectaresToSqm(totalArea)
      } else if (this.unit === 'imperial') {
        return this.acresToSqft(totalArea)
      }
    }

    return totalArea
  }

  /**
   * What is the value for the total area of units on the development (in the current calculation's
   * standard units)?
   *
   * This is used to calculate totals in a "per unit area" calculation.
   *
   * @returns {number}
   */
  getRawTotalUnitSize () {
    throw new Error('You must implement getRawTotalUnitSize')
  }

  translateArea (area, large = false) {
    if (large) {
      if (this.unit === 'metric') {
        return this.sqmToHectares(area)
      } else if (this.unit === 'imperial') {
        return this.sqftToAcres(area)
      }
    }

    return area
  }

  /**
   * The total area of of all built units.
   *
   * @param {bool} large Whether to return the value in large areas (hectares/acres).
   */
  getTotalUnitSize (large = false) {
    return this.translateArea(this.getRawTotalUnitSize(), large)
  }

  // Calculators

  /**
   * Get a monetary total for the total amount of area in the development.
   *
   * Actually based on the total unit size, not total land area.
   *
   * @param {string} field The field to totalise for the area.
   * @param {bool} large  Whether to use large area unit (hectares/acres).
   */
  getTotalForArea (field, large = false) {
    return this.getTotalUnitSize(large) * this.getValue(field)
  }

  getTotalForAverage () {
    throw new Error('You must implement getTotalForAverage')
  }

  getAverage (value, totalForAverage = this.getTotalForAverage()) {
    if (! is.number(value)) {
      console.warn('Number not passed to average...', value)
      value = this.getTotalForArea(value)
    }

    return value / totalForAverage
  }

  // Totals

  getGrossDevelopmentValue () {
    throw new Error('You must implement getGrossDevelopmentValue')
  }

  getBuildingCosts () {
    throw new Error('You must implement getBuildingCosts')
  }

  getProfitAndLandValueCosts () {
    throw new Error('You must implement getProfitAndLandValueCosts')
  }

  getTotalCosts () {
    return this.getBuildingCosts() + this.getProfitAndLandValueCosts()
  }

  getDeveloperProfitTotal () {
    throw new Error('You must implement getDeveloperProfitTotal')
  }

  getLandValue () {
    return this.getGrossDevelopmentValue() - this.getTotalCosts()
  }

  getLandValuePerUnitArea (large = false) {
    return this.getLandValue() / this.getTotalArea(large)
  }

  // Translation

  translateToUnit (newUnit) {
    throw new Error('You must implement translateToUnit')
  }
}
